/**
 * Grouping and filtering for the Linear-style homes list (#603 follow-on
 * 3). Values are the API's (`HomeStatus` / `PropertyType` Display impls on
 * the backend): PascalCase, but matched case- and separator-insensitively.
 */
import { HomeReturn } from '../clients/rust-api-client';
import { ListGroup } from '../shell/GroupedList';

export const HOME_STATUSES = [
  { value: 'Occupied', label: 'Occupied', dot: 'bg-success' },
  { value: 'Vacant', label: 'Vacant', dot: 'bg-fg-faint' },
  { value: 'ForSale', label: 'For sale', dot: 'bg-warning' },
  { value: 'ForRent', label: 'For rent', dot: 'bg-accent' },
] as const;

export const PROPERTY_TYPES = [
  { value: 'SingleFamily', label: 'Single family' },
  { value: 'Townhouse', label: 'Townhouse' },
  { value: 'Condo', label: 'Condo' },
  { value: 'Apartment', label: 'Apartment' },
  { value: 'Other', label: 'Other' },
] as const;

const squash = (s: string) => s.toLowerCase().replace(/[_\s-]/g, '');

export const normalizeHomeStatus = (s: string) =>
  HOME_STATUSES.find((x) => squash(x.value) === squash(s))?.value ?? null;

export const propertyTypeLabel = (t: string) =>
  PROPERTY_TYPES.find((x) => squash(x.value) === squash(t))?.label ?? t;

export const homeAddressLine = (h: HomeReturn) =>
  [h.street, h.street2].filter(Boolean).join(' ');

export const homeLocality = (h: HomeReturn) =>
  [h.city, [h.state, h.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');

export interface HomeFilters {
  statuses: string[];
  types: string[];
  search: string;
}

export const applyHomeFilters = (homes: HomeReturn[], f: HomeFilters) => {
  const q = f.search.trim().toLowerCase();
  return homes.filter((h) => {
    if (f.statuses.length && !f.statuses.includes(normalizeHomeStatus(h.status) ?? '')) return false;
    if (f.types.length && !f.types.some((t) => squash(t) === squash(h.property_type))) return false;
    if (q) {
      const hay = [h.street, h.street2, h.city, h.state, h.zip].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
};

/** Status groups in a fixed order, streets sorted naturally ("2 Oak" before "10 Oak"). */
export const groupHomes = (homes: HomeReturn[]): ListGroup<HomeReturn>[] => {
  const byStreet = [...homes].sort((a, b) =>
    homeAddressLine(a).localeCompare(homeAddressLine(b), undefined, { numeric: true })
  );
  const groups: ListGroup<HomeReturn>[] = HOME_STATUSES.map((s) => ({
    key: s.value,
    label: s.label,
    items: byStreet.filter((h) => normalizeHomeStatus(h.status) === s.value),
  }));
  const other = byStreet.filter((h) => normalizeHomeStatus(h.status) === null);
  if (other.length) groups.push({ key: 'Other', label: 'Other', items: other });
  return groups.filter((g) => g.items.length > 0);
};
